import type { CityLine, CityOrderReview, Keep, SavedDecision } from "../../../api/cityOrders";
import { formatMoney, fromCents, toCents } from "../../../api/money";

/** The City PO drawer's rules, apart from the drawer: which step may be left,
 * and what is sent. The server applies the same rules again on save; these
 * only keep the screen from offering what it will refuse. */

export type CityStep = 1 | 2 | 3 | 4;

export const CITY_STEPS: readonly { step: CityStep; label: string }[] = [
  { step: 1, label: "Drop" },
  { step: 2, label: "Check" },
  { step: 3, label: "Clear" },
  { step: 4, label: "Confirm" },
];

export interface Choice {
  keep: Keep;
  reason: string;
}

export type Choices = Record<string, Choice>;

export function differences(review: CityOrderReview): CityLine[] {
  return review.lines.filter((line) => line.state !== "matched");
}

function overridden(overriding: boolean, reason: string): boolean {
  return overriding && reason.trim() !== "";
}

/** Our BOQ's total once each "use the City's figure" is applied, in cents. An
 * undecided difference counts at our figure, as the server counts it. */
export function totalAfterCents(review: CityOrderReview, choices: Choices): number {
  let total = 0;
  for (const line of review.lines) {
    const choice = choices[line.code];
    if (line.state !== "matched" && choice?.keep === "city") {
      total += toCents(line.city?.amount ?? "0") ?? 0;
    } else if (line.ours !== null) {
      total += toCents(line.ours.amount) ?? 0;
    }
  }
  return total;
}

/** Why the drawer may not leave `step` yet, or null when it may. */
export function holdAt(step: CityStep, review: CityOrderReview | null, choices: Choices, overriding: boolean, overrideReason: string): string | null {
  if (review === null) {
    // No check to go on from: a failed read, or one still to be made again.
    return step === 1 ? "Drop the City's PO to read it" : "Read the City's PO first";
  }
  if (step === 2) {
    const standing = review.holds.filter((hold) => !(hold.overridable && overridden(overriding, overrideReason)));
    if (standing.length === 0) {
      return null;
    }
    return standing.every((hold) => hold.overridable) ? "Override the check above, with a reason, to continue" : "Fix what is held above to continue";
  }
  if (step === 3) {
    for (const line of differences(review)) {
      const choice = choices[line.code];
      if (choice === undefined) {
        return `Decide on ${line.code}`;
      }
      if (choice.keep === "ours" && choice.reason.trim() === "") {
        return `Say why our figure stands for ${line.code}`;
      }
    }
    const after = totalAfterCents(review, choices);
    const city = toCents(review.totals.city_excl_vat) ?? 0;
    if (after > city) {
      return `Our BOQ would come to ${formatMoney(fromCents(after))}, above the City's ${formatMoney(review.totals.city_excl_vat)}`;
    }
  }
  return null;
}

/** A choice that is whole: "keep ours" is not, until it says why. */
export function isComplete(choice: Choice | undefined): choice is Choice {
  return choice !== undefined && (choice.keep === "city" || choice.reason.trim() !== "");
}

/** What is sent: a decision for each difference decided. An incomplete "keep
 * ours" is left out, so saving for later never sends a decision the server
 * would refuse; the office finishes it when they come back. */
export function decisionsBody(review: CityOrderReview, choices: Choices): SavedDecision[] {
  return differences(review)
    .filter((line) => isComplete(choices[line.code]))
    .map((line) => {
      const choice = choices[line.code];
      return { code: line.code, keep: choice.keep, reason: choice.reason.trim() || null };
    });
}

export function choicesFrom(saved: SavedDecision[] | undefined): Choices {
  return Object.fromEntries((saved ?? []).map((decision) => [decision.code, { keep: decision.keep, reason: decision.reason ?? "" }]));
}

/** The choices that still apply to `review`: those on a line that is still a
 * difference. A fresh read can clear a difference the office had decided. */
export function pruneChoices(review: CityOrderReview, choices: Choices): Choices {
  const codes = new Set(differences(review).map((line) => line.code));
  return Object.fromEntries(Object.entries(choices).filter(([code]) => codes.has(code)));
}

/** The server's warning that the PO's W/O is not the one on the BOQ's site. */
export function workOrderMismatch(review: CityOrderReview): boolean {
  return review.warnings.some((warning) => warning.startsWith("This PO's W/O "));
}

/** The refusal that asks for the PO to be read again. */
export function isStale(message: string): boolean {
  return message.includes("changed since this PO was read");
}
